import _ from 'lodash'
import moment from 'moment'

// Format of dates used in queries.
const dateQueryFormat = 'YYYY-MM-DD'
const resultsEvent = 'Search Results Returned'
const TIMEOUT_RESPONSE_CODE = -1

class RecordQueryService {
  constructor(Record, $log, config, $analytics, md5, $q) {
    this.Record = Record
    this.$log = $log
    this.$analytics = $analytics
    this.uri = config.searchEndpoint
    this.queryRunning = false
    this._cancellableQuery = null
    this._queryParams = {}
    this._records = null
    this.responseDataCache = {}
    this._errorMessage = ''
    this.md5 = md5
    this.$q = $q
  }

  get queryParams() {
    return this._queryParams
  }

  set queryParams(queryParams) {
    this._queryParams = queryParams
  }

  get cancellableQuery() {
    return this._cancellableQuery
  }

  set cancellableQuery(cancellableQuery) {
    this._cancellableQuery = cancellableQuery
  }

  cancelPendingQueries() {
    if (this.cancellableQuery) {
      this.cancellableQuery.$cancelRequest()
      this.cancellableQuery = null
    }
    this.stopQueryRunning()
  }

  get queryCancelled() {
    return this.cancellableQuery 
      && this.cancellableQuery.$promise.$$state.status === 2
  }
  get canAddAlert() {
    return this.cancellableQuery
      && this.cancellableQuery.$resolved
      && !this.queryCancelled
      && !this.errorMessage
  }

  startQueryRunning() {
    this.queryRunning = true
  }

  stopQueryRunning() {
    this.queryRunning = false
  }

  query(searchFormCriteria) {
    this.startQueryRunning()
    this.clearRecords()
    this.clearErrorMessage()
    this.queryParams = _(searchFormCriteria)
      .pick(this._validCriteria)
      .tap(this._parseCriteriaDates.bind(this))
      .value()

    const paramsKey = this.hashOf(this.queryParams)
    const cachedRecords = this.responseDataCache[paramsKey]
    cachedRecords ? this.fetchFromCache(paramsKey) : this.fetchFromServer(this.queryParams)
  }

  fetchFromCache(paramsKey) {
    const cachedResponseData = this.responseDataCache[paramsKey]
    this.records = cachedResponseData.records
    this.errorMessage = cachedResponseData.errorMessage
    this.stopQueryRunning()
  }

  fetchFromServer(queryParams) {
    this.clearErrorMessage()
    this._trackSearchStarted(queryParams)
    this.cancellableQuery = this.Record.query({filter: queryParams})
    this.cancellableQuery.$promise
      .then((data) => {
        this._trackSuccess(data, queryParams)
        this._addToCache(data.records, queryParams)
        this.records = data.records
      })
      .catch((errorResponse) => {
        const errorMessage = this.errorFromStatus(errorResponse)
        this._trackFailure(errorResponse, queryParams)
        if (errorResponse.status === TIMEOUT_RESPONSE_CODE) {
            this.errorMessage = errorMessage
            this.stopQueryRunning()
          return
        }
        this._addToCache(null, queryParams, errorMessage)
      })
  }

  get records() {
    return this._records
  }


  get errorMessage() {
    return this._errorMessage
  }

  set errorMessage(errorMessage) {
    this._errorMessage = errorMessage
  }

  clearErrorMessage() {
    this.errorMessage = ''
  }

  clearCache() {
    this.cachedResponseData = {}
  }

  set records(records) {
    this._records = records
  }
  
  clearRecords() {
    this._records = {}
  }

  errorFromStatus(errorResponse) {
    switch (errorResponse.status) {
      case 404: return 'Your account has been locked for the day by going over our record limit. Please contact us if you feel this has been done in error.'
      case 420: return 'Your query is too large, and it will push you over the daily record limit. Please refine your search.'
      case 409: return errorResponse.data || 'Your search returned too many results.  Please refine your query.'
      case  -1: return 'This search took too long and was aborted. Please refine and resubmit your query.'
      default:  return 'Something went wrong.'
    }
  }

  hashOf(queryParams) {
    const paramString = JSON.stringify(queryParams)
    return this.md5.createHash(paramString)
  }

  invalidateCachedItem(paramsKey) {
    this.responseDataCache = _.omit(this.responseDataCache, paramsKey)
  }

  _addToCache(records, queryParams, errorMessage = '') {
    const paramsKey = this.hashOf(queryParams)
    this.responseDataCache[paramsKey] = {
      records: records,
      errorMessage: errorMessage  
    }
    this.errorMessage = errorMessage;
    this.stopQueryRunning()
  }

  _trackSearchStarted(queryParams) {
    this.$log.debug(queryParams)
    this.$analytics.timeEvent('Search Started')
    this.$analytics.eventTrack(
      'Search Started',
      {
        'search parameters': queryParams
      }
    )
  }

  _trackSuccess(data, queryParams) {
    if(data.records && data.records.length) {
      this.$analytics.eventTrack(
        resultsEvent,
          {
            'results returned': data.records.length,
            'result count': data.totalMatchingRecords,
            'search criteria': queryParams
          }
      )
    }
  }

  _trackFailure(errorResponse, queryParams) {
    this.$analytics.eventTrack(
      'Search Results Errored',
      {
        'result' : errorResponse,
        'search criteria': queryParams
      }
    )
  }

  // TODO: date formatting should be written as an angular directive, and update
  // the model directly.
  _parseCriteriaDates(criteria) {
    _.forEach(['startDate', 'endDate'], dateParam => {
      if (_.has(criteria, dateParam)) {
        criteria[dateParam] = moment(criteria[dateParam]).format(dateQueryFormat)
      }
    })
    _.forEach(['addedAtStart', 'addedAtEnd'], dateParam => {
      if (_.has(criteria, dateParam)) {
        criteria[dateParam] = moment(criteria[dateParam]).startOf('day').toISOString()
      }
    })
  }

  _validCriteria(val) {
    // fairly course checking - bools, dates, numbers, not special strings
    // 'any' and 'all', and not empty strings
    return _.isBoolean(val) ||
      _.isDate(val) ||
      (_.isArray(val) && !_.isEmpty(val)) ||
      _.isNumber(val) ||
      (_.isString(val) && !_.isEmpty(val) && !val.match(/^any$|^all$/i))
  }
}

RecordQueryService.$inject = ['Record', '$log', 'config', '$analytics', 'md5', '$q']

export default RecordQueryService
